/**
 * State 01 — Sign in: Start.
 *
 * Combined email + password card. On submit:
 *   - signIn.create({ identifier, password }) — Clerk validates both
 *     in a single network call.
 *   - status === "complete"            → setActive + close dialog
 *   - status === "needs_first_factor"  → check first-factor strategy:
 *       email_code   → prepareFirstFactor + go to state 02
 *       (other)      → go to state 05 (use another method)
 *
 * Copy verbatim from C:/tmp/mio-auth-journey.html state 01. The
 * footer link sends the user to state 06 (sign-up start).
 */

"use client";

import { FormEvent, useState } from "react";

import { useSignIn } from "@clerk/nextjs";

import {
  IconEye,
  IconEyeOff,
  IconWarningTriangle,
} from "./AuthIcons";
import { EllipsisDots } from "./EllipsisDots";
import { activateSession } from "./sessionActivation";
import {
  AuthFlowState,
  AuthMode,
  AuthStep,
  VerifyMode,
  readClerkError,
} from "./useAuthFlow";
import { trackSignupCompleted } from "@/lib/analytics";

type Props = {
  flow: AuthFlowState;
  setIdentifier: (v: string) => void;
  setError: (e: string | null) => void;
  setSubmitting: (b: boolean) => void;
  setVerifyMode: (m: VerifyMode) => void;
  markResend: () => void;
  goTo: (s: AuthStep) => void;
  onAuthenticated: () => void;
  onSwitchMode: (m: AuthMode) => void;
};

export function SignInStartCard({
  flow,
  setIdentifier,
  setError,
  setSubmitting,
  setVerifyMode,
  markResend,
  goTo,
  onAuthenticated,
  onSwitchMode,
}: Props) {
  const { signIn, setActive, isLoaded } = useSignIn();
  const [email, setEmail] = useState(flow.identifier);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!isLoaded || !signIn || !setActive) return;
    setError(null);
    setSubmitting(true);
    setIdentifier(email);
    try {
      const result = await signIn.create({
        identifier: email,
        password,
      });

      if (result.status === "complete" && result.createdSessionId) {
        // 2026-08-29: bounded activation - never an infinite spinner (see
        // sessionActivation.ts for the stale-deployment root cause).
        await activateSession({
          setActive,
          sessionId: result.createdSessionId,
        });
        // C21: through the sign-up gate. Attribution (negotiate vs
        // elsewhere) comes from the marker written when the gate was raised.
        trackSignupCompleted({ method: "sign_in" });
        onAuthenticated();
        return;
      }

      if (result.status === "needs_first_factor") {
        const emailFactor = result.supportedFirstFactors?.find(
          (f): f is typeof f & { emailAddressId: string } =>
            f.strategy === "email_code",
        );
        if (emailFactor) {
          await signIn.prepareFirstFactor({
            strategy: "email_code",
            emailAddressId: emailFactor.emailAddressId,
          });
          markResend();
          setVerifyMode("first");
          goTo("signIn.verifyCode");
          return;
        }
        setError(
          "This account uses a sign-in method we don't support yet. Try resetting your password.",
        );
        return;
      }

      // New-device MFA: Clerk accepted the password (clientTrustState
      // "new") and now demands a second factor. Mio Clerk only has
      // email_code as a second factor; if a future config adds TOTP /
      // SMS / backup-code, surface a clear message.
      if (result.status === "needs_second_factor") {
        const emailSecond = result.supportedSecondFactors?.find(
          (f): f is typeof f & { emailAddressId: string } =>
            f.strategy === "email_code",
        );
        if (emailSecond) {
          await signIn.prepareSecondFactor({
            strategy: "email_code",
            emailAddressId: emailSecond.emailAddressId,
          });
          markResend();
          setVerifyMode("second");
          goTo("signIn.verifyCode");
          return;
        }
        setError(
          "This account needs a verification method we don't support yet. Contact support to recover access.",
        );
        return;
      }

      // Admin-forced password reset: route to the forgot-password card
      // so the user enters the reset email-code flow.
      if (result.status === "needs_new_password") {
        goTo("signIn.forgotPassword");
        return;
      }

      // Identifier unknown / rejected — stay on start with a hint.
      if (result.status === "needs_identifier") {
        setError("Please re-enter your email and try again.");
        return;
      }

      setError("Could not finish sign in. Try another method.");
    } catch (err) {
      setError(readClerkError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={onSubmit} noValidate>
      <div className="mio-auth-card-header">
        <h1>Welcome back</h1>
        <p>Sign in to your Octopusden account</p>
      </div>

      {flow.error ? (
        <div className="mio-auth-error" role="alert">
          <IconWarningTriangle />
          <div>{flow.error}</div>
        </div>
      ) : null}

      <div className="mio-auth-field">
        <div className="mio-auth-field-head">
          <label className="mio-auth-field-label" htmlFor="mio-si-email">
            Email
          </label>
        </div>
        <input
          id="mio-si-email"
          className="mio-auth-input"
          type="email"
          autoComplete="email"
          autoFocus
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>

      <div className="mio-auth-field">
        <div className="mio-auth-field-head">
          <label className="mio-auth-field-label" htmlFor="mio-si-password">
            Password
          </label>
          <button
            type="button"
            className="mio-auth-field-action"
            onClick={() => {
              setIdentifier(email);
              goTo("signIn.forgotPassword");
            }}
          >
            Forgot password?
          </button>
        </div>
        <div className="mio-auth-input-wrap">
          <input
            id="mio-si-password"
            className="mio-auth-input with-eye"
            type={showPassword ? "text" : "password"}
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <button
            type="button"
            className="mio-auth-input-eye"
            aria-label={showPassword ? "Hide password" : "Show password"}
            onClick={() => setShowPassword((v) => !v)}
            tabIndex={-1}
          >
            {showPassword ? <IconEyeOff /> : <IconEye />}
          </button>
        </div>
      </div>

      <button
        type="submit"
        className="mio-auth-cta"
        aria-busy={flow.submitting}
        disabled={
          flow.submitting || email.length === 0 || password.length === 0
        }
      >
        {flow.submitting ? (
          <>
            Signing in
            <EllipsisDots />
          </>
        ) : (
          "Continue"
        )}
      </button>
      <div className="mio-auth-card-foot">
        No account?
        <button type="button" onClick={() => onSwitchMode("sign-up")}>
          Sign up
        </button>
      </div>
    </form>
  );
}
